import { ApiQuery, Filter, FilterOperators } from "~/modules/filters/types";
import { TokenTimeseriesColumns } from "~/modules/token_timeseries/types";
import { MEASURE_SLUGS } from "~/consts";
import {
    CompositionChartConfig,
    TimeseriesChartConfig,
} from "~/modules/charts/types";

// // // //

// Default to bridged token value dollar if measure_slug is not set (edge-case)
const DEFAULT_MEASURE_SLUG: string = MEASURE_SLUGS.bridged_token_value_dollar;

/**
 * getTimeseriesMeasureSlug
 * Reads the separately-stored measure_slug from a loose chart config.
 */
export function getTimeseriesMeasureSlug(
    config: TimeseriesChartConfig | CompositionChartConfig,
): string {
    const value = config.measure_slug;

    if (typeof value === "string" && value.length > 0) {
        return value;
    }

    return DEFAULT_MEASURE_SLUG;
}

/**
 * getTimeseriesQuery
 * Reads the query (without the measure_slug filter) from a loose chart config.
 */
export function getTimeseriesQuery(
    config: TimeseriesChartConfig | CompositionChartConfig,
): ApiQuery {
    return config.query as ApiQuery;
}

/**
 * buildTimeseriesQuery
 * Merges the separately-stored measure_slug back into the query as a
 * measureSlug filter so the result can be sent to the data API.
 */
export function buildTimeseriesQuery(
    config: TimeseriesChartConfig | CompositionChartConfig,
): ApiQuery {
    const query = getTimeseriesQuery(config);
    const measureSlug = getTimeseriesMeasureSlug(config);

    const measureFilter: Filter = {
        field: TokenTimeseriesColumns.measureSlug,
        operator: FilterOperators.Equals,
        value: measureSlug,
    };

    const finalFilter: Filter = {
        operator: "and",
        filters: [measureFilter],
    };
    const filter: Filter | undefined = query.filter;

    if (
        filter?.operator === FilterOperators.And &&
        filter?.filters &&
        filter?.filters.length === 1
    ) {
        finalFilter.filters.push(filter.filters[0]);
    } else if (filter) {
        finalFilter.filters.push(filter);
    }

    return {
        ...query,
        filter: finalFilter,
    };
}
